import { useEffect, useRef, useState, useCallback } from 'react';
import { config } from '../config/env';

export type WebSocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export interface UseWebSocketOptions {
  url?: string;
  reconnect?: boolean;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
  onOpen?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
  protocols?: string | string[];
}

export interface UseWebSocketReturn<T = any> {
  status: WebSocketStatus;
  lastMessage: T | null;
  ws: WebSocket | null;
  sendMessage: (data: string | object) => void;
  connect: () => void;
  disconnect: () => void;
  reconnectCount: number;
  hasMaxRetries: boolean;
}

export function useWebSocket<T = any>(options: UseWebSocketOptions = {}): UseWebSocketReturn<T> {
  const {
    url = config.wsUrl,
    reconnect = true,
    reconnectDelay = 3000,
    maxReconnectAttempts = Infinity,
    onOpen,
    onClose,
    onError,
    protocols,
  } = options;

  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const [lastMessage, setLastMessage] = useState<T | null>(null);
  const [reconnectCount, setReconnectCount] = useState(0);
  const [hasMaxRetries, setHasMaxRetries] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const isConnectingRef = useRef(false);
  
  // Store callbacks in refs to avoid recreating connect callback
  const callbacksRef = useRef({ onOpen, onClose, onError });

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = { onOpen, onClose, onError };
  }, [onOpen, onClose, onError]);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    isConnectingRef.current = false;
    if (reconnectTimeoutRef.current !== null) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (error) {
        // Ignore errors when closing
      }
      wsRef.current = null;
    }
    setStatus('disconnected');
  }, []);

  const connect = useCallback(() => {
    // Prevent duplicate connection attempts
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }
    
    if (isConnectingRef.current) {
      return; // Already connecting
    }

    if (!url || url.trim() === '') {
      setStatus('disconnected');
      return;
    }

    try {
      isConnectingRef.current = true;
      setStatus('connecting');
      shouldReconnectRef.current = true;

      // Remove trailing slash from URL
      const cleanUrl = url.replace(/\/$/, '');
      console.log(`Connecting to WebSocket: ${cleanUrl}`);
      
      const ws = protocols ? new WebSocket(cleanUrl, protocols) : new WebSocket(cleanUrl);

      ws.onopen = (event) => {
        console.log('[WebSocket] Connection opened successfully', {
          url: cleanUrl,
          readyState: ws.readyState,
          protocol: ws.protocol,
          extensions: ws.extensions,
        });
        isConnectingRef.current = false;
        setStatus('connected');
        reconnectAttemptsRef.current = 0;
        setReconnectCount(0);
        setHasMaxRetries(false);
        callbacksRef.current.onOpen?.(event);
      };

      ws.onmessage = (event) => {
        console.log('[WebSocket] Message received', {
          type: typeof event.data,
          length: event.data?.length || 0,
          preview: typeof event.data === 'string' ? event.data.substring(0, 100) : 'binary',
        });
        // Use requestAnimationFrame to batch message processing and avoid blocking
        requestAnimationFrame(() => {
          try {
            const data = JSON.parse(event.data) as T;
            setLastMessage(data);
          } catch {
            setLastMessage(event.data as T);
          }
        });
      };

      ws.onerror = (event) => {
        console.error('[WebSocket] Error occurred', {
          url: cleanUrl,
          readyState: ws.readyState,
          type: event.type,
          error: event,
          timestamp: new Date().toISOString(),
        });
        isConnectingRef.current = false;
        setStatus('error');
        callbacksRef.current.onError?.(event);
      };

      ws.onclose = (event) => {
        console.log('[WebSocket] Connection closed', {
          url: cleanUrl,
          code: event.code,
          reason: event.reason || 'No reason provided',
          wasClean: event.wasClean,
          readyState: ws.readyState,
          timestamp: new Date().toISOString(),
        });
        isConnectingRef.current = false;
        setStatus('disconnected');
        callbacksRef.current.onClose?.(event);

        // Don't reconnect if we're in cleanup (StrictMode) or if connection was closed intentionally
        // Only reconnect for unexpected closures (code 1006) or if wasClean is false
        if (
          shouldReconnectRef.current &&
          reconnect &&
          reconnectAttemptsRef.current < maxReconnectAttempts &&
          (event.code === 1006 || !event.wasClean) // Only reconnect on abnormal closures
        ) {
          reconnectAttemptsRef.current += 1;
          setReconnectCount(reconnectAttemptsRef.current);
          console.log(`[WebSocket] Attempting reconnect ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${reconnectDelay}ms (code: ${event.code}, wasClean: ${event.wasClean})`);

          reconnectTimeoutRef.current = window.setTimeout(() => {
            if (shouldReconnectRef.current) {
              connect();
            }
          }, reconnectDelay);
        } else if (reconnect && reconnectAttemptsRef.current >= maxReconnectAttempts) {
          // Max retries reached - stop auto-reconnecting
          console.warn(`[WebSocket] Max reconnect attempts (${maxReconnectAttempts}) reached. Stopping auto-reconnect.`);
          setHasMaxRetries(true);
        } else if (event.wasClean && event.code !== 1006) {
          console.log('[WebSocket] Clean close, not reconnecting');
        }
      };

      wsRef.current = ws;
    } catch (error) {
      isConnectingRef.current = false;
      setStatus('error');
      console.error('WebSocket connection error:', error);
    }
  }, [url, protocols, reconnect, reconnectDelay, maxReconnectAttempts]);

  const sendMessage = useCallback((data: string | object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      wsRef.current.send(message);
    }
  }, []);

  // Track if this is the initial mount to avoid StrictMode cleanup issues
  const isInitialMountRef = useRef(true);
  const mountCountRef = useRef(0);

  useEffect(() => {
    if (!url || url.trim() === '') {
      return;
    }

    mountCountRef.current += 1;
    const currentMount = mountCountRef.current;
    const isFirstMount = isInitialMountRef.current;
    
    if (isFirstMount) {
      isInitialMountRef.current = false;
      console.log('[WebSocket] First mount detected, delaying connection to avoid StrictMode cleanup');
      
      // Add delay to let StrictMode cleanup complete
      const connectTimer = setTimeout(() => {
        // Only connect if this is still the current mount (not a new one)
        if (currentMount === mountCountRef.current && shouldReconnectRef.current) {
          if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
            console.log('[WebSocket] Initial connection delay complete, connecting now');
            connect();
          }
        }
      }, 150); // Delay to let StrictMode cleanup finish

      return () => {
        clearTimeout(connectTimer);
        // On first cleanup (StrictMode), don't disconnect - just let it clean up naturally
        if (currentMount === 1) {
          console.log('[WebSocket] First mount cleanup (StrictMode), skipping disconnect');
          return;
        }
        // On real cleanup, disconnect
        if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
          console.log('[WebSocket] Cleanup: disconnecting active connection');
          disconnect();
        }
      };
    }

    // For subsequent mounts (URL changes), connect immediately
    console.log('[WebSocket] URL changed or remount, connecting');
    connect();

    return () => {
      // Only disconnect if we have an active connection
      if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
        console.log('[WebSocket] Cleanup: disconnecting active connection');
        disconnect();
      }
    };
  }, [url]); // Only depend on URL - connect/disconnect are stable

  return {
    status,
    lastMessage,
    ws: wsRef.current,
    sendMessage,
    connect: () => {
      // Reset retry count when manually connecting
      reconnectAttemptsRef.current = 0;
      setReconnectCount(0);
      setHasMaxRetries(false);
      shouldReconnectRef.current = true;
      connect();
    },
    disconnect,
    reconnectCount,
    hasMaxRetries,
  };
}
